"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, FileCheck2, House as Home, Eye, Trees, ClipboardCheck, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const safetyProtocols = [{
  icon: Eye,
  title: "Initial site assessment",
  description: "Every Ascot project starts with an on-site survey to map ground conditions, access constraints, private driveways, and proximity to delicate landscaping."
}, {
  icon: Home,
  title: "Property and boundary care",
  description: "Protection measures for exterior brickwork, heritage stone, manicured lawns, and delicate roofing tiles, keeping your grounds safe throughout work."
}, {
  icon: ShieldCheck,
  title: "Safe access planning",
  description: "Custom-planned access configurations designed around your daily routines, security gates, and discreet working zones."
}, {
  icon: ClipboardCheck,
  title: "Formal handover inspection",
  description: "Comprehensive safety checks and a signed handover certificate issued before any trades step onto the scaffold platforms."
}];
const accreditationHighlights = [{
  name: "NASC member",
  detail: "National Access & Scaffolding Confederation full auditing standards for safety and operational compliance."
}, {
  name: "CHAS accredited",
  detail: "Contractors Health and Safety Assessment Scheme verified contractor health, safety, and risk controls."
}];
export default function AscotSafety() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="ascotsafetyandaccess" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <Badge variant="outline" className="mb-4 border-border text-muted-foreground font-normal">
            Ascot and surrounding areas
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-6">
            Scaffolding access for high-value and sensitive residential properties
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Working on substantial homes across Ascot requires careful planning, respectful site conduct, and thorough property protection from day one through to dismantle.
          </p>
        </div>

        {/* Main Grid: Core Context and Safety Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {safetyProtocols.map((protocol, index) => {
          const IconComponent = protocol.icon;
          return <motion.div key={protocol.title} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color]">
                  <CardHeader className="pb-3">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-primary mb-3">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <CardTitle className="text-lg font-default font-semibold text-card-foreground">
                      {protocol.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {protocol.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Middle Detail: Context and Accreditations Banner */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-16">
          
          {/* Property Protection Overview */}
          <div className="lg:col-span-7 bg-muted/50 rounded-xl p-8 border border-border flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 text-primary font-medium text-sm mb-3">
                <Trees className="w-4 h-4" />
                <span>Sensitive surrounding management</span>
              </div>
              <h3 className="text-2xl font-default font-bold text-foreground mb-4">
                Discreet operations for private residences
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                From gated estates to private roads, we understand the standards required by homeowners and managing agents. Our teams coordinate deliveries, establish ground protection boards for stone drives and lawns, and maintain organized, tidy working zones every day.
              </p>
            </div>

            <div className="space-y-3 pt-4 border-t border-border">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <span className="text-xs sm:text-sm text-foreground">Base padding and timber sole boards to guard paving and driveways</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <span className="text-xs sm:text-sm text-foreground">Scheduled delivery times tailored to private road rules</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <span className="text-xs sm:text-sm text-foreground">Debris netting and edge guards where roof lines or gardens are exposed</span>
              </div>
            </div>
          </div>

          {/* Accreditations Box */}
          <div className="lg:col-span-5 bg-card rounded-xl p-8 border border-border shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 text-primary font-medium text-sm mb-3">
                <FileCheck2 className="w-4 h-4" />
                <span>Safety verification</span>
              </div>
              <h3 className="text-2xl font-default font-bold text-card-foreground mb-3">
                Recognised industry accreditations
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                Our safety practices and structural standards are audited independently under industry-recognised bodies.
              </p>

              <div className="space-y-4">
                {accreditationHighlights.map((acc, i) => <div key={acc.name} data-index={i} className="p-4 rounded-lg bg-muted/40 border border-border/70">
                    <div className="font-semibold text-sm text-foreground mb-1">
                      {acc.name}
                    </div>
                    <div className="text-xs text-muted-foreground leading-normal">
                      {acc.detail}
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="pt-6 mt-6 border-t border-border">
              <Button variant="outline" size="sm" asChild className="w-full justify-between">
                <Link to="/HealthSafetyAccreditationsPage">
                  <span>View our safety accreditations</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>

        </div>

        <Separator className="mb-12" />

        {/* Bottom Action Strip */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-card border border-border rounded-xl p-6 sm:p-8">
          <div>
            <h4 className="text-lg font-default font-semibold text-card-foreground mb-1">
              Planning a residential or roofing project in Ascot?
            </h4>
            <p className="text-sm text-muted-foreground">
              Speak with Pete and our experienced team for an honest site assessment and clear quote.
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactRequestQuotePage">
                Request a site visit
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="hidden sm:inline-flex">
              <Link to="/ResidentialScaffoldingPage">
                Residential services
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}